import { useMediaQuery } from "shared/hooks";
import { cn } from "shared/lib";
import { Separator } from "shared/components";

export function PrivacyPolicyPage() {
  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  return (
    <div
      className={cn(
        "flex grow flex-col overflow-y-auto px-5 py-5 md:pt-2",
        !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
      )}
    >
      <div
        className={cn(
          "flex grow flex-col items-center rounded-xl",
          md ? "bg-interfaceBgLightPaper" : "",
        )}
      >
        <div className="text-textPrimary mb-4 mt-4 flex w-[700px] max-w-[700px] flex-col gap-4 md:mb-14 md:mt-10 md:gap-10">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-headingxl">Privacy Policy</h1>
            <p className="text-bodysm text-textSubdued">
              Last updated 06/12/2024
            </p>
          </div>

          <p className="text-bodymd">
            myLot ("we," "us," or "our") is committed to protecting the privacy
            of our users. This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our services.
          </p>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">1. Information we collect</h2>
            <div>
              <p>We collect the following personal information from users:</p>
              <ul className="text-bodymd flex list-outside list-disc flex-col gap-1 pl-6">
                <li>
                  <b>Email address:</b> Collected for account creation and to
                  send a report once the service is completed.
                </li>
                <li>
                  <b>Physical address:</b> Collected to provide our service,
                  specifically to identify relevant planning approvals. This is
                  not necessarily your home address.
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">2. Purpose of data collection</h2>
            <div>
              <p>
                We collect and use your information for the following purposes:
              </p>
              <ul className="text-bodymd flex list-outside list-disc flex-col gap-1 pl-6">
                <li>
                  <b>Email address:</b> Used to create your account and send
                  reports related to our service.
                </li>
                <li>
                  <b>Physical address:</b> Used to determine the planning
                  controls relevant to the location in question.
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">3. Data sharing</h2>
            <p>
              We share your information only with the relevant{" "}
              <b>local council</b> to facilitate the service, specifically in
              relation to planning approvals. We do not share your email or
              physical address with any other third parties.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">
              4. Use of cookies and tracking technologies
            </h2>
            <p>
              We use session cookies and session logging for analytics and to
              improve user experience. We do not use any tracking for
              advertising purposes.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">5. Legal basis for processing</h2>
            <p>
              We rely on <b>contractual necessity</b> as the legal basis for
              processing your personal data. This means that we collect and use
              your information to provide the services you've signed up for.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">6. Data retention</h2>
            <ul className="text-bodymd flex list-outside list-disc flex-col gap-1 pl-6">
              <li>
                <b>Email address:</b> Retained for as long as you maintain an
                account with myLot.
              </li>
              <li>
                <b>Physical address:</b> Anonymised 2 years after the completion
                of our service, unless retained for longer in accordance with
                the relevant council's privacy policy. The retention of data by
                councils will be subject to their own privacy policies.
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">7. User rights</h2>
            <p>
              At this time, we do not offer options for users to access,
              correct, or delete their personal data.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">8. Data security</h2>
            <p>
              We store your data on Amazon Web Services (AWS) and follow all
              security best practices. We are also working toward SOC2
              certification to ensure the highest standards of data protection.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">9. Data localisation</h2>
            <p>
              All personal data collected by myLot is from Australian users, and
              all data is stored within Australia.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">
              10. Changes to this Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes
              will be posted on our website.
            </p>
          </div>

          <Separator orientation="horizontal" />

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Contact details</h2>
            <ul className="text-bodymd flex list-outside list-disc flex-col gap-1 pl-6">
              <li>
                Email:{" "}
                <a
                  className="text-textLink cursor-pointer underline"
                  href="mailto:help@mylot.ai"
                  tabIndex={0}
                >
                  help@mylot.ai
                </a>
              </li>
              <li>
                Website:{" "}
                <a
                  className="text-textLink cursor-pointer underline"
                  href="https://mylot.ai"
                  rel="noreferrer"
                  target="_blank"
                  tabIndex={0}
                >
                  https://mylot.ai
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicyPage;